import { createId } from '@paralleldrive/cuid2';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { smartText } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doAseSetupSignatoryDelete,
  doAseSetupSignatorySave,
} from 'store/actions';
import { AseSetupSignatory } from 'types/Assessments';

export const SignatorySetupModal: React.FC<
  ModalScreenProps<ModalScreens.AseSetupSignatory>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();

  const signatoryIsNew = !route.params.signatory;

  const initValues = React.useRef<AseSetupSignatory>(
    route.params.signatory
      ? {
          id: route.params.signatory.id,
          name: route.params.signatory.name,
          declaration: route.params.signatory.declaration,
          allowUnavailable: route.params.signatory.allowUnavailable,
          requireOnSubmit: route.params.signatory.requireOnSubmit,
          ordering: route.params.signatory.ordering,
        }
      : {
          id: createId(),
          name: '',
          declaration: '',
          allowUnavailable: false,
          requireOnSubmit: false,
          ordering: 1,
        },
  );
  const changes = React.useRef(cloneDeep(initValues.current));

  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleChangeWithNameForceUpdate = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (isDirty.current) {
      if (!changes.current.name.trim()) {
        alert({
          text: 'Name is required',
        });
        return;
      }
      dispatch(
        doAseSetupSignatorySave({
          signatory: changes.current,
          isNew: signatoryIsNew,
        }),
      );
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [alert, dispatch, signatoryIsNew]);

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={signatoryIsNew ? 'New Signatory' : 'Signatory'}
          onPressCancelButton={handleClose}
          onPressSaveButton={isDirty.current ? handleSave : null}
          saveButtonText="Save"
          menuRight={
            !signatoryIsNew
              ? {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    {
                      text: 'Delete Signatory',
                      onPress: () =>
                        dispatch(
                          doAseSetupSignatoryDelete({
                            id: changes.current.id,
                            name: initValues.current.name,
                          }),
                        ),
                      destructive: true,
                      icon: ICONS.TrashCanLight,
                    },
                  ],
                }
              : undefined
          }
        />
      }
    >
      <Card label="Name & Declaration">
        <TextField
          placeholder="Name"
          name="name"
          onChangeWithName={handleChangeWithName}
          value={changes.current.name}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
        <TextAreaField
          placeholder="Declaration"
          name="declaration"
          onChangeWithName={handleChangeWithName}
          value={changes.current.declaration}
          onEsc={handleClose}
        />
      </Card>

      <Card explanation="Require this signatory before the assessment can be submitted">
        <CardRow
          label="Require"
          checkedValue={changes.current.requireOnSubmit}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'requireOnSubmit',
              !changes.current.requireOnSubmit,
            )
          }
        />
      </Card>

      <Card
        explanation={smartText(
          'Allow assessors to mark this signatory as "Not Available"',
        )}
      >
        <CardRow
          label={smartText('Allow "Not Available"')}
          checkedValue={changes.current.allowUnavailable}
          onPress={() =>
            handleChangeWithNameForceUpdate(
              'allowUnavailable',
              !changes.current.allowUnavailable,
            )
          }
        />
      </Card>
    </PageScrollView>
  );
};
